//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { Disposable } from "relay-runtime";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { compact, isEqual } from "lodash";
import { fetchQuery } from "react-relay";

// chrome-wrapper imports
import { IProps, IState } from "./ChromeWrapper.d";
import * as styles from "./ChromeWrapper.styles";

// other components
import { AutopilotListRenderer } from "@components/autopilot-list";
import { CalendarProfileRenderer } from "@components/calendar-profile";
import { ChromeCreateEvent } from "@components/chrome-create-event";
import { ChromeDayRenderer } from "@components/chrome-day";
import { ChromeFatalError } from "@components/chrome-fatal-error";
import { ChromeFeedRenderer } from "@components/chrome-feed";
import { ChromeLogin } from "@components/chrome-login";
import { ChromeMenuDrawer } from "@components/chrome-menu-drawer";
import { ChromeMismatch } from "@components/chrome-mismatch";
import { ChromeNav } from "@components/chrome-nav";
import { ChromePersonalCalendar } from "@components/chrome-personal-calendar";
import { ChromeWelcome } from "@components/chrome-welcome";
import { Event } from "@components/event";
import { EventSchedulerRenderer } from "@components/event-scheduler";
import { FastCrawlErrorCard } from "@components/fast-crawl-error-card";
import { FeedScheduler } from "@components/feed-scheduler";
import { FlowStatePoller } from "@components/flow-state-poller";
import { OnboardingNUX } from "@components/onboarding-nux";
import { PlanStatusRefetch } from "@components/plan-status-refetch";
import { PricingDialog } from "@components/pricing-dialog";
import { ShareBestTimesLinkModal } from "@components/scheduling-link";
import { SettingsDialog } from "@components/settings-dialog";
import { TeamHealthDashboardRenderer } from "@components/team-health-dashboard";
import { TutorialBox } from "@components/tutorial-box";

// subscriptions
import {
  updateFlowStateSubscription,
  updateEventInNearbyWeekChangesSubscription,
  updateEventParentBaseWrappers,
} from "@subscriptions";

// mutations
import {
  preAddOrgTags,
  updateFlagsForUser,
  updateSetting,
  saveTeamUserSettings,
  preSetEventFlexibility,
} from "@mutations";

// queries
import {
  suggestedTimesQuery,
  suggestedTimesRendererQuery,
} from "@components/event-scheduler-time-input";

// constants
import { paths } from "@clockwise/client-commons/src/constants/site";
import { API_URL } from "@clockwise/client-commons/src/constants/api";

// util
import { isValidJson } from "@clockwise/web-commons/src/util/json.util";
import { PostMessageManager } from "@util/post-message.util";
import {
  ChromeIdleThreshold,
  QueryStringParams,
} from "@clockwise/web-commons/src/util/post-message-common.util";
import { getCurrentOrg, getOrgCrawlFailure } from "@util/org.util";
import {
  track,
  TrackingEvents,
  page,
  PageEvents,
  group,
  startFullStory,
} from "@util/analytics.util";
import { getPreCookie } from "@clockwise/web-commons/src/util/pre-cookie.util";
import { Tags as EventTags, EFlex } from "@util/event-tag.util";
import { palette } from "@clockwise/web-commons/src/styles/color.styles";
import {
  ChromeAppWrapperView,
  ChromeViewStack,
  ChromeSite,
  getChromeSite,
} from "@util/chrome-extension.util";
import { fromGlobalId } from "@util/graphql.util";
import {
  ZonedMoment,
  getRenderTimeZone,
  setRenderTimeZone,
  setCurrentTimeZone,
} from "@util/time-zone.util";
import { FlagNamespaces, UserFlags, validateFlagsErrorable, isFlagSet } from "@util/flags.util";
import { logger } from "@util/logger.util";
import { chromeExtensionInstalledSetting } from "@util/org-settings.util";
import {
  initIdealDefrag,
  initDecorateEvents,
  initFilterRooms,
  initView,
  initExperiments,
} from "@util/clockwise-debugger.util";
import { haveIntroduced } from "@clockwise/web-commons/src/util/have-introduced.util";
import { getBulkExperiments } from "@util/experiment.util";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { translate } from "@clockwise/client-commons/src/util/translations";
import { getValue } from "@src/util/errorable.util";
import { FlagsProvider } from "@clockwise/web-commons/src/util/FlagsContext";

// state
import { getFastCrawlFlowState, getOnboardingFlowState } from "@state/onboarding";
import { TOnboardingFlowState, TFastCrawlFlowState } from "@actions/onboarding.actions";
import { onboardingOpen, jwt } from "@state/local-storage";
import { setShouldFetchSuggestedTimes, setCalendarUrl } from "@actions/chrome-extension.actions";
import { getReduxStore } from "@state/redux-store";
import { IEditEventInfo } from "@reducers/chrome-extension.reducer";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import { DayOffsetTimeSlot } from "@clockwise/client-commons/src/util/time-slot";
import { ExperimentsProvider } from "@clockwise/web-commons/src/util/ExperimentsContext";
import { PreEventFlexibility } from "@clockwise/schema";

//////////////////
// COMPONENT
//////////////////

const CLOCKWISE_HISTORY_LOCATION_HASH = "history";
export class ChromeWrapper extends React.Component<IProps, IState> {
  private idleInterval: ReturnType<typeof setInterval>;
  private refetchFeedInterval: ReturnType<typeof setInterval>;
  private webappUpdateInterval: ReturnType<typeof setInterval>;
  private closedOrOpenInterval: ReturnType<typeof setInterval>;
  private hovered = false;
  private chromeAppWrapperLastActiveMillis = 0;
  private previousView: ChromeAppWrapperView | null; // only used to apply styles, can be outside state
  private epwDisposable?: Disposable;
  private fastCrawlDisposable?: Disposable;
  private eventWeekDisposable?: Disposable;
  private onboardingFinishedToInviteCounter = 0;
  private skipNextMatch = false;
  private hasSentShowTeamTip = false;
  private hasStartedFS = false;

  // must track locally since both subscription and poller can return state
  // before our update to redux has caused a componentWillReceiveProps
  private didFinishRefetch = false;
  private didTeammatesRefetch = false;
  private didUserRefetch = false;

  constructor(props: IProps) {
    super(props);
    this.selectCreateEventAnnotation = this.selectCreateEventAnnotation.bind(this);

    // pop up onboarding splash if we are fast crawling and need onboarding slides
    const authed = !!props.viewer.user;
    const org = getCurrentOrg(this.props.viewer);

    // set state
    this.state = {
      authed,
      onboardingDialogOpen: false,
      createEventAnnotation: "",
      createEventAllowedStart: null,
      createEventAllowedEnd: null,
      idle: false,
    };

    PostMessageManager.sendAuthenticated(authed);

    if (org) {
      PostMessageManager.sendOrgDomains(org.domains);
    }

    if (authed && validateFlagsErrorable(props.viewer.user.chromeExtensionFlagsErrorable)) {
      PostMessageManager.sendUserFlags(props.viewer.user.chromeExtensionFlagsErrorable.flags);
    }

    this.maybeSetOnboardingFlowStateFromProps(props);
    this.maybeSetFastCrawlFlowStateFromProps(props);
  }

  private maybeSetOnboardingFlowStateFromProps = (props: IProps) => {
    const onboardingFlowState = props.viewer.user && getOnboardingFlowState(props.viewer.user);
    if (!onboardingFlowState) {
      return;
    }

    const isFinishedToInvite =
      this.props.onboardingFlowStateCurrentStatus === "Finished" &&
      onboardingFlowState.current.state === "Invite";
    // update state if diverging from current props
    if (onboardingFlowState.current.state !== this.props.onboardingFlowStateCurrentStatus) {
      // HACK: sometimes i get into an infinite loop that goes between Finished and Invite
      // this is a hack for external users to stop it
      if (isFinishedToInvite) {
        this.onboardingFinishedToInviteCounter++;
        if (this.onboardingFinishedToInviteCounter > 1) {
          if (this.onboardingFinishedToInviteCounter === 2) {
            logger.error(`Onboarding flowstate going from Finished to Invite multiple times.`);
          }
          return;
        }
      }
      this.props.setOnboardingFlowState(onboardingFlowState as TOnboardingFlowState);
      PostMessageManager.sendOnboardingFlowStateCurrentStatus(onboardingFlowState.current.state);

      // if we are going from Finished to another state, assume initial load
      // and open onboarding dialog if we dont have a fatal error
      if (
        this.props.onboardingFlowStateCurrentStatus === "Finished" &&
        !this.props.fastCrawlFatalState &&
        onboardingOpen.get()
      ) {
        this.props.closeSettingsDrawers();
        this.openOnboardingDialog();
      }
    }
  };

  private maybeSetFastCrawlFlowStateFromProps = (props: IProps) => {
    const org = getCurrentOrg(props.viewer);
    const fastCrawlFlowState = org && getFastCrawlFlowState(org);
    if (!fastCrawlFlowState) {
      return;
    }

    this.maybeSetFastCrawlFlowState(fastCrawlFlowState);
  };

  private maybeSetFastCrawlFlowState = (
    fastCrawlFlowState?: ISchema.IFlowState | null,
    error?: Error,
  ) => {
    if (error) {
      logger.error("Error polling flow state", error);
    }

    if (!fastCrawlFlowState) {
      return;
    }

    // only update the flow state on new state or new error
    if (
      fastCrawlFlowState.current.state !== this.props.fastCrawlFlowStateCurrentStatus ||
      fastCrawlFlowState.current.errorMessage !== this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.props.setFastCrawlFlowState(fastCrawlFlowState as TFastCrawlFlowState);
      PostMessageManager.sendFastCrawlFlowStateCurrentStatus(fastCrawlFlowState.current.state);
    }

    // refetch if its needed (always check later statuses first to avoid multiple refetches)
    if (
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFinishedRefetchComplete &&
      !this.didFinishRefetch
    ) {
      // it would probably be more optimal to set this on complete of the refresh, but we dont
      // want to accidentally refetch again if this function gets called before we complete
      // also passing a callback to a refetch, might not result in the callback getting called!
      this.didFinishRefetch = true;
      this.props.setFastCrawlFinishedRefetchComplete();
      this.fastCrawlRefetch();
    } else if (
      this.props.fastCrawlTeammatesIdentifiedComplete &&
      !this.props.fastCrawlTeammatesIdentifiedRefetchComplete &&
      !this.didTeammatesRefetch
    ) {
      this.didTeammatesRefetch = true;
      this.props.setFastCrawlTeammatesIdentifiedRefetchComplete();
      this.fastCrawlRefetch();
    } else if (
      this.props.fastCrawlUserCrawledComplete &&
      !this.props.fastCrawlUserCrawledRefetchComplete &&
      !this.didUserRefetch
    ) {
      this.didUserRefetch = true;
      this.props.setFastCrawlUserCrawledRefetchComplete();
      this.fastCrawlRefetch();
    }

    // start or stop subscription?
    if (
      this.fastCrawlDisposable &&
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.fastCrawlDisposable.dispose();
      delete this.fastCrawlDisposable;
    } else if (
      !this.fastCrawlDisposable &&
      (!this.props.fastCrawlFinished || this.props.fastCrawlFlowStateErrorMessage)
    ) {
      this.subscribeFastCrawl();
    }
  };

  private maybeHandleCalendarUrl = (calendarUrl: string, oldCalendarUrl: string) => {
    if (!calendarUrl || calendarUrl === oldCalendarUrl || calendarUrl.indexOf("?") === -1) {
      return;
    }

    // if we're not onboarding, maybe the calendar url has query params for other ui to show
    const search = calendarUrl.split("?")[1];
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(QueryStringParams.EventId)) {
      const eventIdBase64 = searchParams.get(QueryStringParams.EventId);

      if (eventIdBase64) {
        const [externalEventId, calendarId] = atob(eventIdBase64).split(" ");
        if (externalEventId && calendarId) {
          if (!this.props.chromeExtensionOpen) {
            PostMessageManager.toggleSidebar(true, false);
          }

          // We need to set currentViewOfEventWasSetByUrlParam to `true` so that
          // the other view logic (like `detectIdle`) can react to it properly
          // Note that setActiveViewEvent's currentViewOfEventWasSetByUrlParam arg is false by default
          // Thus if user clicks and changes event view, the currentViewOfEventWasSetByUrlParam flag will be set back to false
          // And the idle detection logic will be react accordingly.
          const currentViewOfEventWasSetByUrlParam = true;
          this.props.setActiveViewEvent(
            externalEventId,
            calendarId,
            false,
            false,
            false,
            currentViewOfEventWasSetByUrlParam,
          );
        }
      }
    }

    if (
      searchParams.has(QueryStringParams.Invite) ||
      searchParams.has(QueryStringParams.Slack) ||
      searchParams.has(QueryStringParams.SmartHolds) ||
      searchParams.has(QueryStringParams.PersonalCalendar) ||
      searchParams.has(QueryStringParams.ColorCoding) ||
      searchParams.has(QueryStringParams.Plans) ||
      searchParams.has(QueryStringParams.Lunch) ||
      searchParams.has(QueryStringParams.MeetingBreaks)
    ) {
      // if we are not open, open
      if (!this.props.chromeExtensionOpen) {
        PostMessageManager.toggleSidebar(true, false);
      }
      this.props.setSettingsDialogOpen(true);

      if (searchParams.has(QueryStringParams.Invite)) {
        this.props.toggleExpanded("expandInvites");
      } else if (searchParams.has(QueryStringParams.Slack)) {
        this.props.toggleExpanded("expandIntegrations");
      } else if (searchParams.has(QueryStringParams.SmartHolds)) {
        this.props.toggleExpanded("expandSmartHolds");
      } else if (searchParams.has(QueryStringParams.PersonalCalendar)) {
        this.props.toggleExpanded("expandPersonalCalendar");
      } else if (searchParams.has(QueryStringParams.ColorCoding)) {
        this.props.toggleExpanded("expandColorCoding");
      } else if (searchParams.has(QueryStringParams.Plans)) {
        this.props.toggleExpanded("expandPaidPlans");
      } else if (searchParams.has(QueryStringParams.Lunch)) {
        this.props.toggleExpanded("expandSmartHolds");
        this.props.toggleExpanded("expandLunch");
      } else if (searchParams.has(QueryStringParams.MeetingBreaks)) {
        this.props.toggleExpanded("expandMeetingBreaks");
      }
    }
    if (searchParams.has(QueryStringParams.Autopilot)) {
      // if we are not open, open
      if (!this.props.chromeExtensionOpen) {
        PostMessageManager.toggleSidebar(true, false);
      }
      setTimeout(() => {
        this.props.setActiveViewAutopilotList();
        PostMessageManager.clearQueryParams(QueryStringParams.Autopilot);
      }, 1000);
    }
    if (searchParams.has(QueryStringParams.InstallEvent)) {
      // google tag manager
      const dataLayer = window && (window as any).dataLayer;
      if (dataLayer) {
        dataLayer.push({ event: "chromeExtensionInstalled" });
      } else {
        logger.error("failed to push event to dataLayer");
      }
      const preCookie = getPreCookie();
      track(TrackingEvents.CHROME_EXTENSION.INSTALLED, {
        windowUserEmail: this.props.windowUserEmail,
        latestUtms: preCookie.latestUtms,
      });
      PostMessageManager.clearQueryParams(QueryStringParams.InstallEvent);
    }

    // check if we should open chrome extension
    if (/[?&]cwopen=/.test(window.location.search)) {
      const newPath = window.location.href.replace(/[?&]cwopen=[^&]+/, "");
      window.history.replaceState({ path: newPath }, "", newPath);
      PostMessageManager.openSidebar();
    }

    getReduxStore().dispatch(setCalendarUrl(""));
  };

  private maybeShowTeamCalendarTip = (props: IProps) => {
    const { viewer } = props;

    const org = getCurrentOrg(viewer);

    const onboardingFlowState = getOnboardingFlowState(viewer.user);

    if (org && onboardingFlowState && onboardingFlowState.current.state === "Finished") {
      const flagsErrorable =
        props.viewer && props.viewer.user && props.viewer.user.chromeExtensionFlagsErrorable;

      const userTeams =
        (org.userTeams &&
          org.userTeams.__typename !== "GraphEntityError" &&
          org.userTeams.list.length &&
          org.userTeams.list) ||
        [];

      const primaryTeam =
        org.primaryTeam && org.primaryTeam.__typename !== "GraphEntityError" && org.primaryTeam;

      const matchedUserTeam =
        userTeams.find(
          (team) =>
            team.userSettings &&
            team.userSettings.__typename !== "GraphEntityError" &&
            team.userSettings.useTeamCalendar &&
            team.settings &&
            team.settings.__typename !== "GraphEntityError" &&
            team.settings.manageTeamCalendar &&
            !!(primaryTeam && team.teamId === primaryTeam.teamId && team.teamCalendarId),
        ) ||
        userTeams.find((team) => !!team.teamCalendarId) ||
        null;

      const shouldShowCalendarTip =
        matchedUserTeam &&
        matchedUserTeam.teamCalendarId &&
        !isFlagSet(UserFlags.Onboarding.TeamCalendar.name, flagsErrorable) &&
        !this.hasSentShowTeamTip;

      if (shouldShowCalendarTip) {
        PostMessageManager.showTeamCalendarTip(matchedUserTeam.teamCalendarId);
        this.hasSentShowTeamTip = true;
      }
    }
  };

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  private syncRedux(props: IProps) {
    const org = getCurrentOrg(props.viewer);

    if (!props.match || this.skipNextMatch || !org) {
      this.skipNextMatch = false;
      return;
    }

    const { params } = props.match;
    const { pathname, hash } = props.match.location;

    if (pathname.startsWith(`${paths.webApp}/autopilot-list`)) {
      props.setActiveView(ChromeAppWrapperView.AutopilotList);
    } else if (pathname.startsWith(`${paths.webApp}/calendar-profile`)) {
      props.setActiveViewCalendarProfile(atob(params.calendarId), params.viewWeek);
    } else if (pathname.startsWith(`${paths.webApp}/team-analytics`)) {
      props.setActiveView(ChromeAppWrapperView.TeamHealth);
    } else if (pathname.startsWith(`${paths.webApp}/event`)) {
      try {
        const externalEventId = atob(params.encodedExternalEventId);
        const calendarId =
          (params.fullView && params.fullView !== "f"
            ? atob(params.fullView)
            : params.encodedCalendarId &&
              params.encodedCalendarId !== "f" &&
              atob(params.encodedCalendarId)) || org.primaryOrgCalendar;

        const showClockwiseHistory = !!hash?.includes(CLOCKWISE_HISTORY_LOCATION_HASH);

        props.setActiveViewEvent(
          externalEventId,
          calendarId,
          false,
          undefined,
          showClockwiseHistory,
        );
      } catch (e) {
        // do nothing if atob fails
      }
    } else if (pathname.startsWith(`${paths.webApp}/calendar`)) {
      props.setActiveView(ChromeAppWrapperView.Day);
    }
  }

  private syncLocation(props: IProps) {
    const org = getCurrentOrg(props.viewer);

    if (!props.router || !props.match || !org) {
      return;
    }

    switch (props.activeView) {
      case ChromeAppWrapperView.AutopilotList:
        props.router.push(`${paths.webApp}/autopilot-list${props.match.location.hash}`);
        break;
      case ChromeAppWrapperView.CalendarProfile:
        const { calendarProfile } = props;

        props.router.push(
          `${paths.webApp}/calendar-profile/${btoa(calendarProfile.calendarId)}${
            calendarProfile.viewWeek ? `/${calendarProfile.viewWeek}` : ""
          }${props.match.location.hash}`,
        );
        break;
      case ChromeAppWrapperView.TeamHealth:
        props.router.push(`${paths.webApp}/team-analytics${props.match.location.hash}`);
        break;
      case ChromeAppWrapperView.Event:
        const { calendarId, externalEventId, showClockwiseHistory } = props.activeViewEvent;

        const maybeCalendarId = calendarId !== org.primaryOrgCalendar ? `${btoa(calendarId)}` : "";

        let locationHash = props.match.location.hash as string;
        if (!showClockwiseHistory) {
          locationHash = locationHash
            .split("#/")
            .filter((hash) => hash !== CLOCKWISE_HISTORY_LOCATION_HASH)
            .join("#/");
        }

        const url = compact([paths.webApp, "event", btoa(externalEventId), maybeCalendarId]).join(
          "/",
        );
        props.router.push(`${url}${locationHash}`);

        break;
      case ChromeAppWrapperView.Day:
        props.router.push(`${paths.webApp}/calendar${props.match.location.hash}`);
        break;
      default:
        props.router.push(`${paths.webApp}${props.match.location.hash}`);
        break;
    }

    this.skipNextMatch = true;
  }

  private hasViewStateChanged(nextProps: IProps) {
    switch (nextProps.activeView) {
      case ChromeAppWrapperView.CalendarProfile:
        return (
          this.props.calendarProfile.calendarId !== nextProps.calendarProfile.calendarId ||
          this.props.calendarProfile.viewWeek !== nextProps.calendarProfile.viewWeek
        );
      case ChromeAppWrapperView.Event:
        return (
          this.props.activeViewEvent.externalEventId !== nextProps.activeViewEvent.externalEventId
        );
      default:
        return false;
    }
  }

  public UNSAFE_componentWillMount() {
    // if chrome extension is installed, send setting to services
    const org = getCurrentOrg(this.props.viewer);
    if (org && !chromeExtensionInstalledSetting.isOn(org) && this.props.chromeExtensionInstalled) {
      const setting = chromeExtensionInstalledSetting.get(org);
      const value = ["true"];

      if (setting) {
        updateSetting(this.props.relay.environment, { setting, value }, () => "no-op", logger.warn);
      }
    }

    this.syncRedux(this.props);
  }

  public componentDidMount() {
    page(PageEvents.CHROME_EXTENSION);
    this.startIdlePoll();
    this.startWebappUpdatePoll();
    this.startRefetchPoll();
    this.startClosedOrOpenPoll();

    // debugger interface
    initIdealDefrag(this);
    initDecorateEvents(this);
    initFilterRooms(this);
    const userStatus = this.props.viewer && this.props.viewer.user && this.props.viewer.user.status;
    initView(userStatus, this);
    initExperiments(this);

    const org = getCurrentOrg(this.props.viewer);

    if (org && org.id) {
      // analytics
      group(`Org::${fromGlobalId(org.id).id}`, {
        name: org.name,
        created: new ZonedMoment(org.createdTime),
      });

      if (org.experiments) {
        this.props.setExperiments(org.experiments);
      } else {
        logger.warn("User has org object but no experiments");
      }

      // sync org with chrome-extension
      PostMessageManager.syncActiveOrg(org);

      if (
        org.eventColoringSettingsErrorable &&
        org.eventColoringSettingsErrorable.__typename === "EventColoringSettings"
      ) {
        PostMessageManager.sendColorSettings(org.eventColoringSettingsErrorable);
      }

      PostMessageManager.sendUser(this.props.viewer.user);

      // Save user scheduling links to Redux
      if (this.props.viewer.user.schedulingLinks) {
        this.handleSchedulingLinks(this.props.viewer.user.schedulingLinks);
      }

      // Save linkName, if present, to Redux
      if (this.props.viewer.user.linkNameErrorable) {
        this.props.setLinkName(getValue(this.props.viewer.user.linkNameErrorable)?.linkName || "");
      }

      this.sendUserGoalsToExtension(org);

      PostMessageManager.sendExperiments(org);
    }

    // handle calendar URL
    this.maybeHandleCalendarUrl(this.props.calendarUrl, "");

    this.maybeShowTeamCalendarTip(this.props);

    // add mouse listener to body, to ensure we catch off hovers
    document.body.addEventListener("mouseover", this.onMouseOverBody);
    window.addEventListener("mouseleave", this.onMouseLeaveWindow);

    // subscribe
    if (this.props.activeView === ChromeAppWrapperView.Day) {
      this.subscribeEpw();
    }

    if (getChromeSite() === ChromeSite.Gcal) {
      this.subscribeEventWeek();
    }

    const flagsErrorable =
      this.props.viewer &&
      this.props.viewer.user &&
      this.props.viewer.user.chromeExtensionFlagsErrorable;

    if (
      this.props.isChromeExtension &&
      flagsErrorable &&
      validateFlagsErrorable(flagsErrorable) &&
      !isFlagSet(UserFlags.Onboarding.HasInstalledChromeExtension.name, flagsErrorable)
    ) {
      updateFlagsForUser(
        this.props.relay.environment,
        {
          flagNamespace: FlagNamespaces.ChromeExtension,
          flagNames: [UserFlags.Onboarding.HasInstalledChromeExtension.name],
          enable: true,
        },
        () => null,
        () => null,
      );
    }

    // detect sudo and timezone change
    if (
      isImpersonated(jwt.get()) &&
      org &&
      org.userWorkingHours &&
      org.userWorkingHours.workingHours.timeZone &&
      (getRenderTimeZone() !== org.userWorkingHours.workingHours.timeZone ||
        getRenderTimeZone() !== org.userWorkingHours.workingHours.timeZone)
    ) {
      window.alert(`Changing time zone to ${org.userWorkingHours.workingHours.timeZone}`);
      setRenderTimeZone(org.userWorkingHours.workingHours.timeZone);
      setCurrentTimeZone(org.userWorkingHours.workingHours.timeZone);
    }
  }

  public componentWillUnmount() {
    clearInterval(this.idleInterval);
    clearInterval(this.refetchFeedInterval);
    clearInterval(this.closedOrOpenInterval);
    clearInterval(this.webappUpdateInterval);

    // remove mouse listener on body
    document.body.removeEventListener("mouseover", this.onMouseOverBody);
    window.removeEventListener("mouseleave", this.onMouseLeaveWindow);

    if (this.epwDisposable) {
      this.epwDisposable.dispose();
      delete this.epwDisposable;
    }

    if (this.fastCrawlDisposable) {
      this.fastCrawlDisposable.dispose();
      delete this.fastCrawlDisposable;
    }

    if (this.eventWeekDisposable) {
      this.eventWeekDisposable.dispose();
      delete this.eventWeekDisposable;
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.selectedMoment !== this.props.selectedMoment) {
      this.subscribeEpw();
    } else if (!this.epwDisposable && this.props.activeView === ChromeAppWrapperView.Day) {
      this.subscribeEpw();
    }

    if (prevProps.shouldRefetchDueToPlanUpgrade !== this.props.shouldRefetchDueToPlanUpgrade) {
      this.refetchDueToPlanUpgrade();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { createEventAnnotation, createEventAllowedStart, createEventAllowedEnd } = this.state;
    const {
      preAnnotationInput,
      preAnnotationMoveRangeV3,
      preEventFlexibility,
      preEventKey,
      preEventCalendarId,
    } = nextProps;
    const nextOrg = getCurrentOrg(nextProps.viewer);
    const org = getCurrentOrg(this.props.viewer);

    // handle calendar url
    this.maybeHandleCalendarUrl(nextProps.calendarUrl, this.props.calendarUrl);

    this.maybeShowTeamCalendarTip(nextProps);

    // update flow states
    this.maybeSetOnboardingFlowStateFromProps(nextProps);
    this.maybeSetFastCrawlFlowStateFromProps(nextProps);

    // detect logout
    if (!nextProps.viewer.user && this.props.viewer.user) {
      this.setState({
        authed: false,
      });
      this.closeSettingsDialog();
      PostMessageManager.sendAuthenticated(false);
    }

    if (nextOrg && nextOrg !== org) {
      this.sendUserGoalsToExtension(nextOrg);

      if (
        nextOrg.eventColoringSettingsErrorable &&
        nextOrg.eventColoringSettingsErrorable.__typename === "EventColoringSettings"
      ) {
        PostMessageManager.sendColorSettings(nextOrg.eventColoringSettingsErrorable);
      }
    }

    // maybe sync active org with chrome-extension
    if (nextOrg && !this.props.shouldSyncActiveOrg && nextProps.shouldSyncActiveOrg) {
      PostMessageManager.syncActiveOrg(nextOrg);
      this.props.setShouldSyncActiveOrg(false);
    }

    // detect login
    if (nextProps.viewer.user && !this.props.viewer.user) {
      if (nextOrg && nextOrg.id) {
        group(`Org::${fromGlobalId(nextOrg.id).id}`, {
          name: nextOrg.name,
          created: new ZonedMoment(nextOrg.createdTime),
        });
      }

      this.setState({ authed: true, onboardingDialogOpen: onboardingOpen.get() });
      PostMessageManager.sendAuthenticated(true);

      // Save user scheduling links to Redux
      if (nextProps.viewer.user.schedulingLinks) {
        this.handleSchedulingLinks(nextProps.viewer.user.schedulingLinks);
      }

      // Save linkName, if present, to Redux
      if (nextProps.viewer.user.linkNameErrorable) {
        nextProps.setLinkName(getValue(nextProps.viewer.user.linkNameErrorable)?.linkName || "");
      }
    }

    // Detect suggested times needed, fetch them, return to sender.
    if (nextOrg && this.props.relay.environment && nextProps.shouldFetchSuggestedTimes) {
      // We did the thing, don't do it again until requested, which is when new event details are entered.
      getReduxStore().dispatch(setShouldFetchSuggestedTimes(false));

      const crxVersion = getReduxStore().getState().webExtension.webExtensionVersion;
      const haveBulkExperiments = haveIntroduced("BulkExperiments", crxVersion);
      const fetchExtraSuggestions = haveBulkExperiments;

      try {
        const { editEventInfo } = this.props;
        this.fetchSchedulingSuggestions(
          editEventInfo,
          nextOrg.id,
          nextOrg.primaryOrgCalendar,
          fetchExtraSuggestions ? 9 : 3,
        );
      } catch (err) {
        logger.error("Failed to fetch suggested times with error:", err);
        PostMessageManager.sendSuggestedTimesForEvent({
          slots: undefined,
          qualifiers: undefined,
          queryRecommendation: undefined,
        });
      }
    }

    // detect annotate on event creation
    const hasTags = preAnnotationInput.tags.length > 0 || !!createEventAnnotation;
    if (nextOrg && nextOrg.id && preAnnotationInput !== this.props.preAnnotationInput && hasTags) {
      const input = preAnnotationInput;
      if (input.tags.length === 0 && !!createEventAnnotation) {
        input.tags = [{ name: EventTags.MovementType, value: createEventAnnotation }];

        if (createEventAllowedStart) {
          preAnnotationMoveRangeV3.allowedStartTimeInclusive = {
            timeSlot: createEventAllowedStart.timeSlot as ISchema.TimeSlot,
            dayOffset: createEventAllowedStart.dayOffset,
          };
        }

        if (createEventAllowedEnd) {
          preAnnotationMoveRangeV3.allowedEndTimeInclusive = {
            timeSlot: createEventAllowedEnd.timeSlot as ISchema.TimeSlot,
            dayOffset: createEventAllowedEnd.dayOffset,
          };
        }
      }

      preAddOrgTags(
        this.props.relay.environment,
        nextOrg.id,
        input,
        preAnnotationMoveRangeV3,
        this.onPreAnnotateSuccess.bind(this, preAnnotationInput, input.tags),
        this.onPreAnnotateFailure,
      );

      // reset the annotation to use for the next create event
      this.setState({ createEventAnnotation: "" });
    }

    // detect event flexibility on event creation
    if (
      nextOrg?.id &&
      preEventFlexibility !== this.props.preEventFlexibility &&
      preEventKey !== this.props.preEventKey
    ) {
      if (preEventKey === null || preEventFlexibility === null) {
        return;
      }

      preSetEventFlexibility(
        this.props.relay.environment,
        nextOrg.id,
        preEventCalendarId,
        preEventKey,
        preEventFlexibility,
        (res) => {
          this.onPreSetEventFlexibilitySuccess(preEventFlexibility, res);
        },
        (err) => {
          this.onPreSetEventFlexibilityFailure(err);
        },
      );
    }

    // detect set user flag for inline autopilot
    if (nextProps.setUserFlagMillis > this.props.setUserFlagMillis) {
      updateFlagsForUser(
        this.props.relay.environment,
        {
          // TODO: we probably shouldnt be hardcoding the namespace, but this post-message only will ever come from the chrome extension
          flagNamespace: FlagNamespaces.ChromeExtension,
          flagNames: [nextProps.userFlag],
          enable: true,
        },
        () => null,
        () => logger.error("Failed to set user flag"),
      );
    }

    // detect calendar unsubscribed for team settings
    if (
      nextOrg &&
      nextProps.unsubscribedCalendarIdMillis > this.props.unsubscribedCalendarIdMillis
    ) {
      const matchedTeam =
        nextOrg.userTeams &&
        nextOrg.userTeams.__typename !== "GraphEntityError" &&
        nextOrg.userTeams.list.find(
          (team) => team.teamCalendarId === nextProps.unsubscribedCalendarId,
        );

      if (matchedTeam) {
        saveTeamUserSettings(
          nextProps.relay.environment,
          { orgRelayId: nextOrg.id, teamId: matchedTeam.teamId, useTeamCalendar: false },
          () => null,
          () => null,
        );

        track(TrackingEvents.TEAMS.TEAM_SETTINGS_USER_UNSUBSCRIBED_TEAM_CALENDAR, {
          unsubscribedCalendarId: matchedTeam.teamCalendarId,
        });
      }
    }

    // detect settings button click
    if (nextProps.settingsDialogOpen && !this.props.settingsDialogOpen) {
      track(TrackingEvents.SETTINGS.SETTINGS_DIALOG_OPEN);
    }

    // detect chrome menu button click
    if (nextProps.menuDrawerOpen && !this.props.menuDrawerOpen) {
      track(TrackingEvents.CHROME_MENU.CHROME_MENU_DRAWER_OPEN);
    }

    // detect extension open/closed
    if (nextProps.chromeExtensionOpen !== this.props.chromeExtensionOpen) {
      if (nextProps.chromeExtensionOpen) {
        track(TrackingEvents.CHROME_EXTENSION.OPEN);
      } else {
        track(TrackingEvents.CHROME_EXTENSION.CLOSED);
      }
    }

    // detect scheduler open
    if (nextProps.chromeSchedulerButtonClicked !== this.props.chromeSchedulerButtonClicked) {
      // track
      const extensionAlreadyOpen = nextProps.chromeExtensionOpen;
      track(TrackingEvents.SCHEDULING.INLINE_SCHEDULE_CLICKED, {
        extensionAlreadyOpen,
      });

      // if not open, open
      if (!extensionAlreadyOpen) {
        PostMessageManager.openSidebar(true);
      }

      // the scheduler manages its own state manually, so we need to be on a view
      // with the scheduler in order to trigger it from chrome
      if (
        this.getCurrentView() !== ChromeAppWrapperView.Feed &&
        this.getCurrentView() !== ChromeAppWrapperView.Day
      ) {
        this.props.setActiveView(ChromeAppWrapperView.Feed);
        setTimeout(() => {
          this.props.setSchedulerButtonClicked(nextProps.chromeSchedulerEmails);
        }, 300);
      }
    }

    // detect pricing dialog open call from chrome extension
    if (nextProps.openPricingDialogMillis > this.props.openPricingDialogMillis) {
      this.props.setPricingDialogOpen(true, undefined);
    }

    const nextCalendarId = nextOrg && nextOrg.primaryOrgCalendar;
    const calendarId = org && org.primaryOrgCalendar;

    // detect if we need to resubscribe to event week changes
    if (nextCalendarId !== calendarId) {
      this.subscribeEventWeek();
    }

    // detect changes to experiments
    if (nextOrg && !isEqual((org || {}).experiments, nextOrg.experiments)) {
      this.props.setExperiments(nextOrg.experiments);

      PostMessageManager.sendExperiments(nextOrg);
    }

    if (this.props.activeView !== nextProps.activeView || this.hasViewStateChanged(nextProps)) {
      this.syncLocation(nextProps);
    } else if (
      this.props.match &&
      nextProps.match &&
      this.props.match.location.pathname !== nextProps.match.location.pathname
    ) {
      this.syncRedux(nextProps);
    }
  }

  private handleSchedulingLinks = (links: ISchema.ISchedulingLink[]) => {
    this.props.setSchedulingLinks(links);

    const url =
      this.props.mode === "extension" ? this.props.calendarUrl || "" : window.location.href;
    const search = url.split("?")[1];
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(QueryStringParams.ShareSchedulingLink)) {
      // if we are not open, open
      if (!this.props.chromeExtensionOpen) {
        PostMessageManager.toggleSidebar(true, false);
      }
      setTimeout(() => {
        this.props.setOpenShareLinkModal(true);
        PostMessageManager.clearQueryParams(QueryStringParams.ShareSchedulingLink);
      }, 500);
    }
  };

  private fetchSchedulingSuggestions = (
    editEventInfo: IEditEventInfo,
    orgId: string,
    primaryOrgCalendar: string,
    numSuggestions = 3,
  ) => {
    const startDateArr = editEventInfo.startDate.split("");
    startDateArr.splice(4, 0, "-");
    startDateArr.splice(7, 0, "-");
    const startDateFormatted = startDateArr.join("");

    const endDateArr = editEventInfo.endDate.split("");
    endDateArr.splice(4, 0, "-");
    endDateArr.splice(7, 0, "-");
    const endDateFormatted = endDateArr.join("");

    const attendees = editEventInfo.attendees || [];

    // If the query has no attendees, or single attendee of just the current user, declare this as a TaskHold.
    const eventTraits = [];
    if (attendees.filter((attendee) => attendee !== primaryOrgCalendar).length === 0) {
      eventTraits.push(ISchema.EventTrait.TaskHold);
    }

    const inputQuery = {
      invitees: attendees.map((invitee: string) => ({
        calendarId: invitee,
        required: true,
      })),
      minutesDuration: editEventInfo.duration,
      timeZone: getRenderTimeZone(),
      minSlot: {
        date: startDateFormatted,
        timeSlot: "T_00_00",
      },
      maxSlot: {
        date: endDateFormatted,
        timeSlot: "T_23_30",
      },
      roomsOverride: [],
      limit: numSuggestions,
      seenSlots: [],
      includeRoom: editEventInfo.needRoom,
      traits: eventTraits,
    } as ISchema.ISchedulingQueryInput;
    const suggestedTimesVariables = {
      orgRelayId: orgId,
      query: inputQuery,
    };
    const rendererVariables = {
      orgRelayId: orgId,
      calendarIds: [
        primaryOrgCalendar,
        ...inputQuery.invitees.map((invitee) => invitee.calendarId),
      ],
    };
    const suggestionsPromise = fetchQuery(
      this.props.relay.environment,
      suggestedTimesQuery,
      suggestedTimesVariables,
    ).then((res: ISchema.IOrgEdge) => {
      if (res?.node) {
        const schedulingResponse = res.node.schedulingResponseErrorable;
        if (schedulingResponse && schedulingResponse.__typename !== "GraphEntityError") {
          return schedulingResponse;
        }
      }
      logger.error("Failed to fetch suggested times");
      return;
    });
    const peopleAndRoomsFetchPromise = fetchQuery(
      this.props.relay.environment,
      suggestedTimesRendererQuery,
      rendererVariables,
    ).then((res: ISchema.IOrgEdge) => {
      const peopleAndRooms = [null, null] as [ISchema.IPersonList | null, ISchema.IRoom[] | null];
      if (res?.node) {
        const personsResponse = res.node.personsByCalendarIdsErrorable || [];
        if (personsResponse && personsResponse?.__typename !== "GraphEntityError") {
          peopleAndRooms[0] = personsResponse;
        }
        const workingGroupsResponse = res.node.workingGroups || [];
        if (workingGroupsResponse?.edges?.length > 0) {
          peopleAndRooms[1] = workingGroupsResponse?.edges?.[0].node.rooms || [];
        }
      } else {
        logger.error("Failed to fetch persons or rooms for suggested times");
      }

      return peopleAndRooms;
    });
    Promise.all([suggestionsPromise, peopleAndRoomsFetchPromise]).then((values) => {
      const [suggestions, peopleAndRoomsFetch] = values;
      const slots = suggestions?.slots || [];
      const qualifiers = suggestions?.qualifiers || [];
      const peopleList = peopleAndRoomsFetch?.[0]?.list || [];
      const rooms = peopleAndRoomsFetch?.[1] || [];
      const queryRecommendation = suggestions?.queryRecommendation;

      PostMessageManager.sendSuggestedTimesForEvent({
        slots,
        qualifiers,
        queryRecommendation,
        rooms,
        people: peopleList,
      });
      return;
    });
  };

  private sendUserGoalsToExtension(org: ISchema.IOrg) {
    if (!org) {
      return;
    }

    let focusTimePerWeek = 0;

    if (
      org.shadowCalendarSettingsErrorable?.__typename === "ShadowCalendarSettings" &&
      org.shadowCalendarSettingsErrorable?.focusTimeSyncEnabled
    ) {
      if (org.shadowCalendarSettingsErrorable.focusTimeSyncAll) {
        focusTimePerWeek = -1; // Chrome Extension knows how to consume this.
      } else {
        focusTimePerWeek = org.shadowCalendarSettingsErrorable.focusTimeIdealMinutesPerWeek || 0;
      }
    }

    // Appending Focus Time goal, which is for some reason stored elsewhere in shadowCalendarSettings, into userGoals
    // for the sake of simplifying communication with the Chrome Extension.
    // Syncall "All my available Focus Time" -> -1
    // No focus time scheduling -> 0
    // Max number of hours per week -> number greater than 0
    PostMessageManager.sendUserGoals(
      org?.userGoals?.edges?.map((goalEdge) => goalEdge.node) || [],
      focusTimePerWeek,
    );
  }

  private subscribeEpw = () => {
    const org = getCurrentOrg(this.props.viewer);

    if (!org) {
      return;
    }

    if (this.epwDisposable) {
      this.epwDisposable.dispose();
      delete this.epwDisposable;
    }

    const weekStart = this.props.selectedMoment.clone().startOf("week");

    const input: ISchema.IUpdateEventParentBaseWrappersSubscriptionInput = {
      orgRelayId: org.id,
      calendarIds: org.primaryOrgCalendar ? [org.primaryOrgCalendar] : [],
      dates: new Array(7)
        .fill(null)
        .map((_d, i) => weekStart.clone().add(i, "days").format("YYYY-MM-DD")),
      timeZone: this.props.selectedMoment.timeZone,
      includeAllDay: true,
    };

    this.epwDisposable = updateEventParentBaseWrappers(this.props.relay.environment, input);
  };

  private subscribeFastCrawl = () => {
    const { relay, viewer } = this.props;
    const org = getCurrentOrg(viewer);
    const flowState = org && getFastCrawlFlowState(org);

    if (this.fastCrawlDisposable) {
      return;
    }

    const flowStateRelayId = flowState && flowState.id;

    if (flowStateRelayId) {
      this.fastCrawlDisposable = updateFlowStateSubscription(relay.environment, {
        flowStateRelayId,
      });
    } else {
      logger.warn("failed to subscribe to fast crawl updates due to missing user or flowstate id");
    }
  };

  private subscribeEventWeek = () => {
    if (this.eventWeekDisposable) {
      this.eventWeekDisposable.dispose();
      delete this.eventWeekDisposable;
    }

    const org = getCurrentOrg(this.props.viewer);

    if (!org) {
      return;
    }

    const calendarIds = [org.primaryOrgCalendar];

    this.eventWeekDisposable = updateEventInNearbyWeekChangesSubscription(
      this.props.relay.environment,
      { calendarIds, orgRelayId: org.id },
      (change) => PostMessageManager.sendReloadEvents([change.calendarId], change.affectedWeeks),
    );
  };

  private fastCrawlRefetch = () => {
    // modern
    this.props.relay.refetch(
      {},
      {},
      (error) => {
        if (error) {
          logger.error("Could not refetch for fast crawl", error);
        }
      },
      { force: true },
    );
  };

  private checkWebappUpdate = () => {
    // fetch latest update timestamps
    if (window.fetch && this.props.mode !== "webapp") {
      window.fetch(`${API_URL}/update`, { credentials: "include" }).then((response: any) => {
        response.text().then((text: string) => {
          const parsed = (isValidJson(text) && JSON.parse(text)) || null;
          if (parsed && parsed.chromeAppUpdateTimes) {
            PostMessageManager.sendWebappUpdate(parsed.chromeAppUpdateTimes);
          }
        });
      });
    } else if (this.props.mode !== "webapp") {
      logger.error("Failed to fetch /update, fetch not found on window");
    }
  };

  private onPreAnnotateSuccess = (
    preAnnotationInput: ISchema.IPreAnnotationInput,
    tags: ISchema.ITagInput[],
  ) => {
    const movementTag = tags.find((tag) => tag.name === EventTags.MovementType);
    if (movementTag && (movementTag.value === EFlex.Day || movementTag.value === EFlex.Week)) {
      this.props.snackBarOpen("Set flexibility preferences!");
    }
    const flexibility = tags.find((tag) => tag.name === EventTags.MovementType);

    // analytics
    track(TrackingEvents.DEFRAG.ANNOTATE_ON_CREATE, {
      ...preAnnotationInput,
      flexibility: flexibility && flexibility.value,
    });
  };

  private onPreAnnotateFailure = () =>
    this.props.snackBarOpen("Failed to save flexibility preferences");

  private onPreSetEventFlexibilitySuccess = (
    preEventFlexibility: PreEventFlexibility,
    response?: any,
  ) => {
    if (response?.preSetEventFlexibility?.successful) {
      this.props.snackBarOpen("Set flexibility preferences!");

      track(TrackingEvents.FLEXIBLE_MEETING.SET_FLEXIBLE_ON_CREATE, {
        preEventFlexibility,
      });
    }
  };

  private onPreSetEventFlexibilityFailure = (err: any) => {
    this.props.snackBarOpen("Failed to save flexibility preferences");
    logger.error("Unable to save preSetEventFlexibility", err);
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private startWebappUpdatePoll = () => {
    this.checkWebappUpdate();
    this.webappUpdateInterval = setInterval(this.checkWebappUpdate, 3600000); // 60 minutes (avoid everyone at once)
  };
  private startIdlePoll = () => {
    this.idleInterval = setInterval(this.detectIdle, 1000);
  };

  private startRefetchPoll = () => {
    // 15 minutes = 900,000 millis
    this.refetchFeedInterval = setInterval(this.props.refetchFeed, 900000);
  };

  private startClosedOrOpenPoll = () => {
    // 60 minutes = 3,600,000
    this.trackClosedOrOpen();
    this.closedOrOpenInterval = setInterval(this.trackClosedOrOpen, 3600000);
  };

  private trackClosedOrOpen = () => {
    // Given how frequently this can fire, this is a very heavy tracking event that tracks whether or not a browser with
    // the extension installed and enabled has the extension open, user signed in or not, every 60 minutes.
    // Must enable with an endDate passed into track() to very deliberately enable and then disable.
    const endDate = new Date("2021-12-22");
    const trackOpenClosed = this.props.chromeExtensionOpen
      ? TrackingEvents.CHROME_EXTENSION.POLL_IS_OPEN
      : TrackingEvents.CHROME_EXTENSION.POLL_IS_CLOSED;
    track(trackOpenClosed, undefined, endDate);
  };

  private detectIdle = () => {
    const now = new Date().valueOf();
    const lastMillis = this.chromeAppWrapperLastActiveMillis;
    // if our state isn't idle but both the component and extension are idle, change state
    if (
      !this.state.idle &&
      now - lastMillis > ChromeIdleThreshold &&
      this.props.chromeExtensionIdle
    ) {
      this.setState({ idle: true });
      // When the calendarUrl has an event id we want to keep showing the view of the event
      // Thus skip changing the active view when the event view was set by a URL param
      const {
        activeViewEvent: { currentViewOfEventWasSetByUrlParam },
      } = getReduxStore().getState().webExtension;
      if (!this.hovered && !currentViewOfEventWasSetByUrlParam) {
        this.props.setActiveView(this.props.selectedTabView);
      }
    } else if (
      this.state.idle &&
      (now - lastMillis <= ChromeIdleThreshold || !this.props.chromeExtensionIdle)
    ) {
      this.setState({ idle: false });
    }
  };

  private selectCreateEventAnnotation = (
    createEventAnnotation: string,
    createEventAllowedStart: DayOffsetTimeSlot | null,
    createEventAllowedEnd: DayOffsetTimeSlot | null,
  ) => {
    this.setState({ createEventAnnotation, createEventAllowedStart, createEventAllowedEnd });
  };

  private onHover = (hovered: boolean, event?: React.MouseEvent<HTMLDivElement>) => {
    // the real solution would be to append this method to the <body> in index.ejs, but that looks like too much work for now
    // this workaround exists because material ui appends popovers and whatnot into the body, not chrome-wrapper, thus
    // the app cuts off popovers that you are hovered in
    // so if we are mouse leaving chrome-wrapper, into something still in the body, keep hovered state true
    const isHovered =
      (!hovered &&
        event &&
        event.relatedTarget &&
        (event.relatedTarget as Node).nodeType === Node.ELEMENT_NODE && // check if the browser returned a node or a cross-origin error obj
        document.body.contains(event.relatedTarget as HTMLElement)) ||
      hovered;

    // post message
    PostMessageManager.sendWebExtensionHovered(isHovered);

    // store current state
    this.hovered = isHovered;
  };

  private openOnboardingDialog = () => {
    // local state
    onboardingOpen.set(true);
    this.setState({ onboardingDialogOpen: true });
  };

  private closeOnboardingDialog = () => {
    // local state
    onboardingOpen.set(false);
    this.setState({ onboardingDialogOpen: false });
    track(TrackingEvents.ONBOARDING.ONBOARDING_DISMISS);
  };

  private closeSettingsDialog = () => {
    // set redux state
    this.props.setSettingsDialogOpen(false);
    this.props.closeSettingsDrawers();
  };

  private onMouseLeaveWindow = (_e: MouseEvent) => {
    this.onHover(false);
  };

  private onMouseOverBody = (e: Event) => {
    // failsafe - if we manage to hover on the body, we are no longer on the extension
    this.onHover(e.target !== document.body);
  };

  private onMouseMove = () => {
    const now = new Date().valueOf();
    this.chromeAppWrapperLastActiveMillis = now; // store locally to prevent too many renders
  };

  private onClick = () => {
    const now = new Date().valueOf();
    PostMessageManager.sendWebExtensionClicked(now);

    // fullstory will not start recording until a user actually clicks within the sidebar
    // afterwards, we let fullstory manage inactivity and sessions
    if (!this.hasStartedFS) {
      this.hasStartedFS = true;
      startFullStory(this.props.viewer?.user);
    }
  };

  private onScrollContent = (event: React.UIEvent<HTMLDivElement>) => {
    this.props.setContentScrollTop(event.currentTarget.scrollTop);
  };

  private isFatalOnboardingView = (view: ChromeAppWrapperView) => {
    return (
      view === ChromeAppWrapperView.NoAuth ||
      view === ChromeAppWrapperView.NoOrg ||
      view === ChromeAppWrapperView.CalendarMismatch ||
      view === ChromeAppWrapperView.GoogleContactSharingDisabled ||
      view === ChromeAppWrapperView.GoogleApiAccessDisabled ||
      view === ChromeAppWrapperView.NoCrawl ||
      view === ChromeAppWrapperView.ShouldCrawl ||
      view === ChromeAppWrapperView.PersonalCalendar
    );
  };

  private getCurrentView = () => {
    const {
      viewer,
      calendarMismatch,
      createEvent,
      windowUserEmail,
      eventSchedulerViewActive,
      onboardingFinished,
    } = this.props;
    const { authed } = this.state;

    if (!authed) {
      return ChromeAppWrapperView.NoAuth;
    }

    const org = getCurrentOrg(viewer);

    const { personalCalendar, orgCrawlFailure } = getOrgCrawlFailure(viewer, windowUserEmail);
    const mostRecentActive = this.props.activeView;

    if (!org) {
      return ChromeAppWrapperView.NoOrg;
    } else if (calendarMismatch) {
      return ChromeAppWrapperView.CalendarMismatch;
    } else if (personalCalendar) {
      return ChromeAppWrapperView.PersonalCalendar;
    } else if (orgCrawlFailure) {
      switch (orgCrawlFailure) {
        case "GoogleContactSharingDisabled":
          return ChromeAppWrapperView.GoogleContactSharingDisabled;
        case "GoogleApiAccessDisabled":
          return ChromeAppWrapperView.GoogleApiAccessDisabled;
        case "ShouldCrawl":
          return ChromeAppWrapperView.ShouldCrawl;
        case "NoCrawl":
          return ChromeAppWrapperView.NoCrawl;
        case "NoExternalOrganization":
          return ChromeAppWrapperView.NoExternalOrganization;
        default:
          return ChromeAppWrapperView.GenericCrawlFailure;
      }
    } else if (!onboardingFinished) {
      return ChromeAppWrapperView.Onboarding;
    } else if (createEvent) {
      return ChromeAppWrapperView.CreateEvent;
    } else if (eventSchedulerViewActive) {
      return ChromeAppWrapperView.EventScheduler;
    } else {
      return mostRecentActive;
    }
  };

  private getBackgroundColor = (view: ChromeAppWrapperView) => {
    switch (view) {
      case ChromeAppWrapperView.GoogleContactSharingDisabled:
      case ChromeAppWrapperView.GoogleApiAccessDisabled:
      case ChromeAppWrapperView.ShouldCrawl:
      case ChromeAppWrapperView.GenericCrawlFailure:
      case ChromeAppWrapperView.NoExternalOrganization:
      case ChromeAppWrapperView.NoCrawl:
      case ChromeAppWrapperView.NoAuth:
      case ChromeAppWrapperView.Onboarding:
      case ChromeAppWrapperView.TeamHealth:
      case ChromeAppWrapperView.Feed:
        return palette.paleGrey;
      default:
        return palette.white;
    }
  };

  private getContainerStyle = (view: ChromeAppWrapperView): React.CSSProperties => {
    switch (view) {
      case ChromeAppWrapperView.Event:
        return {
          paddingLeft: 0,
          paddingRight: 0,
        };
      case ChromeAppWrapperView.Day:
        return {
          overflowY: "unset",
          paddingLeft: 0,
          paddingRight: 0,
        };
      case ChromeAppWrapperView.EventScheduler:
        return {
          overflowY: "unset",
        };
      default:
        return {};
    }
  };

  private refetchDueToPlanUpgrade = () => {
    if (this.props.shouldRefetchDueToPlanUpgrade) {
      this.props.relay.refetch(
        {},
        {},
        (error) => {
          if (error) {
            logger.error("Could not refetch after plan upgrade", error);
          }
          this.props.updateShouldRefetch(false);
        },
        { force: true },
      );
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderPricingDialog = (org: ISchema.IOrg | null) => {
    const { pricingDialogOpen, teamId } = this.props.pricingDialogState;

    if (!pricingDialogOpen || !org) {
      return "";
    }

    const primaryTeamId =
      (org.primaryTeam &&
        org.primaryTeam.__typename !== "GraphEntityError" &&
        org.primaryTeam.teamId) ||
      undefined;

    return <PricingDialog org={org} teamId={teamId || primaryTeamId} />;
  };

  private renderView = (view: ChromeAppWrapperView) => {
    const { viewer, fatalError, onboardingFinished } = this.props;
    const { authed } = this.state;

    // very much stop now if we have a fatal error
    if (fatalError) {
      return <ChromeFatalError />;
    }

    // stop now if we arent authed
    if (!authed) {
      return <ChromeLogin viewer={viewer} />;
    }

    const org = getCurrentOrg(viewer);
    if (
      !org ||
      (this.state.onboardingDialogOpen &&
        this.props.onboardingLoaded &&
        !this.isFatalOnboardingView(view) &&
        !onboardingFinished)
    ) {
      // note: NoOrg view handled below, but this will almost certainly happen first
      return null;
    }

    // default to an empty array if we dont have a primary email
    const calendarIds = org.primaryOrgCalendar ? [org.primaryOrgCalendar] : [];
    const onboardingView = this.props.fastCrawlFatalState ? (
      <FastCrawlErrorCard view={view} org={org} />
    ) : (
      <ChromeWelcome
        user={viewer.user}
        onboardingDialogOpen={this.state.onboardingDialogOpen}
        openOnboardingDialog={() => {
          this.openOnboardingDialog();
          track(TrackingEvents.ONBOARDING.ONBOARDING_CONTINUE);
        }}
      />
    );

    if (view === ChromeAppWrapperView.Onboarding || this.props.fastCrawlFatalState) {
      return onboardingView;
    }

    // render
    switch (view) {
      case ChromeAppWrapperView.CalendarMismatch:
        return <ChromeMismatch calendarMismatch={this.props.calendarMismatch} />;
      case ChromeAppWrapperView.PersonalCalendar:
        return <ChromePersonalCalendar user={viewer.user} org={org} />;
      case ChromeAppWrapperView.NoOrg:
      case ChromeAppWrapperView.GoogleContactSharingDisabled:
      case ChromeAppWrapperView.GoogleApiAccessDisabled:
      case ChromeAppWrapperView.ShouldCrawl:
      case ChromeAppWrapperView.NoCrawl:
      case ChromeAppWrapperView.GenericCrawlFailure:
      case ChromeAppWrapperView.NoExternalOrganization:
        return <FastCrawlErrorCard view={view} org={org} />;
      case ChromeAppWrapperView.EventScheduler:
        return <EventSchedulerRenderer environment={this.props.relay.environment} />;
      case ChromeAppWrapperView.CreateEvent:
        return (
          <ChromeCreateEvent
            org={org}
            selectCreateEventAnnotation={this.selectCreateEventAnnotation}
          />
        );
      case ChromeAppWrapperView.Event:
        return (
          <Event
            externalEventId={this.props.activeViewEvent.externalEventId}
            calendarId={this.props.activeViewEvent.calendarId}
            environment={this.props.relay.environment}
            org={org}
          />
        );
      case ChromeAppWrapperView.TeamHealth:
        return <TeamHealthDashboardRenderer environment={this.props.relay.environment} org={org} />;
      case ChromeAppWrapperView.Feed:
        return <ChromeFeedRenderer environment={this.props.relay.environment} />;
      case ChromeAppWrapperView.Day:
        return (
          <ChromeDayRenderer calendarIds={calendarIds} environment={this.props.relay.environment} />
        );
      case ChromeAppWrapperView.CalendarProfile:
        return <CalendarProfileRenderer environment={this.props.relay.environment} org={org} />;
      case ChromeAppWrapperView.AutopilotList:
        return (
          <AutopilotListRenderer
            environment={this.props.relay.environment}
            orgId={org.id}
            calendarId={org.primaryOrgCalendar}
          />
        );
      default:
        return <div />;
    }
  };

  private maybeRenderSettingsDialog = () => {
    const { settingsDialogOpen } = this.props;

    if (!settingsDialogOpen) {
      return <span />;
    }

    PostMessageManager.clearQueryParams(QueryStringParams.Invite);
    PostMessageManager.clearQueryParams(QueryStringParams.Slack);
    PostMessageManager.clearQueryParams(QueryStringParams.SmartHolds);
    PostMessageManager.clearQueryParams(QueryStringParams.PersonalCalendar);
    PostMessageManager.clearQueryParams(QueryStringParams.ColorCoding);

    return (
      <SettingsDialog
        environment={this.props.relay.environment}
        onClose={this.closeSettingsDialog}
      />
    );
  };

  private maybeRenderMenuDrawer = (view: ChromeAppWrapperView) => {
    if (view === ChromeAppWrapperView.NoAuth || view === ChromeAppWrapperView.NoOrg) {
      return <span />;
    }
    const { viewer, menuDrawerOpen } = this.props;

    return <ChromeMenuDrawer viewer={viewer} open={menuDrawerOpen} />;
  };

  private maybeRenderFAB = (view: ChromeAppWrapperView, org: ISchema.IOrg | null) => {
    const { viewer, fatalError, onboardingFinished } = this.props;

    if (!org || !viewer || !viewer.user || fatalError || !onboardingFinished) {
      return;
    }

    const user = viewer.user;

    switch (view) {
      case ChromeAppWrapperView.Day:
      case ChromeAppWrapperView.Feed:
      case ChromeAppWrapperView.TeamHealth:
        return <FeedScheduler currentView={view} user={user} org={org} />;
      default:
      // exit to outer void return
    }

    return;
  };

  private maybeRenderTutorialBox = (view: ChromeAppWrapperView) => {
    // if we don't have a user, we cant render the tutorial box
    if (view === ChromeAppWrapperView.NoAuth || view === ChromeAppWrapperView.NoOrg) {
      return <span />;
    }

    return <TutorialBox view={view} user={this.props.viewer.user} />;
  };

  private maybeRenderOnboardingDialog = (view: ChromeAppWrapperView) => {
    if (!this.state.onboardingDialogOpen || this.isFatalOnboardingView(view)) {
      // bail right away if it's not open or if we're in a view that doesn't make sense
      return null;
    }
    const { viewer, onboardingFinished } = this.props;
    const org = getCurrentOrg(viewer);
    const onboardingState = getOnboardingFlowState(this.props.viewer.user);

    if (!org || !onboardingState || onboardingFinished) {
      return null;
    }

    return (
      <OnboardingNUX
        onboardingState={onboardingState}
        environment={this.props.relay.environment}
        onRequestClose={this.closeOnboardingDialog}
        org={org}
      />
    );
  };

  private viewTransitionDirection = (newView: ChromeAppWrapperView) => {
    const newViewStackIndex = ChromeViewStack.indexOf(newView);
    const prevViewStackIndex =
      this.previousView === null ? -1 : ChromeViewStack.indexOf(this.previousView);

    this.previousView = newView; // IMPORTANT!

    const baseEndIndex = 2;
    const withinBase =
      newViewStackIndex >= 0 &&
      newViewStackIndex <= baseEndIndex &&
      prevViewStackIndex >= 0 &&
      prevViewStackIndex <= baseEndIndex;

    if (newViewStackIndex === -1 || prevViewStackIndex === -1 || withinBase) {
      return "none"; // no animation
    } else if (newViewStackIndex < prevViewStackIndex) {
      return "out"; // going down the stack, so current view slides out
    } else {
      return "in"; // going up the stack, so current view slides in
    }
  };

  public render() {
    const { viewer, chromeExtensionOpen, relay, fastCrawlRunning } = this.props;
    const { authed } = this.state;
    const previousView = this.previousView;
    const view = this.getCurrentView();
    const backgroundColor = this.getBackgroundColor(view);
    const org = getCurrentOrg(viewer);
    const webAppFlagsErrorable = this.props.viewer.user?.webAppFlagsErrorable;
    const defaultView = this.props.selectedTabView;
    const modernContentStyle: React.CSSProperties = {
      ...styles.modernContent,
      ...this.getContainerStyle(view),
    };
    const featureFlags = getBulkExperiments(org);

    // address Safari bug aparent during onboarding conflict between "overflow: hidden scroll" with a set z-index
    // issie => https://app.asana.com/0/357266512179263/1202200571716523/f
    if (view === ChromeAppWrapperView.Event) {
      modernContentStyle.zIndex = "unset";
    }

    // transition-direction
    const transitionDirection = this.viewTransitionDirection(view);
    const fastCrawlFlowState = org ? getFastCrawlFlowState(org) : undefined;

    const containerClassName =
      this.props.mode === "extension"
        ? this.props.classes.modernContainer
        : this.props.classes.modernContainerWebapp;

    return (
      <ExperimentsProvider experiments={featureFlags}>
        <FlagsProvider namespace={FlagNamespaces.Webapp} flagsErrorable={webAppFlagsErrorable}>
          <TranslationContext.Provider value={translate()}>
            <div className={`view-transition-${transitionDirection}`}>
              <TransitionGroup>
                <CSSTransition key={view} timeout={300} classNames="slide">
                  <div
                    className={containerClassName}
                    style={{ background: backgroundColor }}
                    onMouseEnter={() => this.onHover(true)}
                    onMouseLeave={(event) => this.onHover(false, event)}
                    onMouseOver={() => this.onHover(true)}
                    onMouseMove={() => this.onMouseMove()}
                    onClick={() => this.onClick()}
                  >
                    <ChromeNav
                      view={view}
                      defaultView={defaultView}
                      previousView={previousView}
                      authed={authed}
                      environment={relay.environment}
                      org={org}
                    />
                    {this.maybeRenderTutorialBox(view)}
                    <div style={modernContentStyle} onScroll={this.onScrollContent}>
                      {this.maybeRenderFAB(view, org)}
                      {this.renderView(view)}
                    </div>
                    <div style={chromeExtensionOpen ? styles.nop : styles.closedIndicator} />
                    {this.maybeRenderMenuDrawer(view)}
                    {this.maybeRenderSettingsDialog()}
                    <ShareBestTimesLinkModal link={this.props.shareBestTimesLink} />
                  </div>
                </CSSTransition>
              </TransitionGroup>
              {this.maybeRenderOnboardingDialog(view)}
              {this.maybeRenderPricingDialog(org)}
              {fastCrawlRunning && fastCrawlFlowState ? (
                <FlowStatePoller
                  flowState={fastCrawlFlowState}
                  onFlowStateUpdate={this.maybeSetFastCrawlFlowState}
                />
              ) : null}
              {org ? <PlanStatusRefetch org={org} /> : ""}
            </div>
          </TranslationContext.Provider>
        </FlagsProvider>
      </ExperimentsProvider>
    );
  }
}
